import { AppData } from '@lightningjs/sdk/src/Application';
import { WidgetNames } from 'config/routes';
import {
  ErrorCode,
  reportError,
} from 'services/analytics/reportingServicePage';
import { BrazeMessage } from 'types/braze';
import { MessageCallbackProps } from './NotificationService';

/* 
Docs: 
  https://github.com/braze-inc/braze-web-sdk/blob/master/README.md
  https://js.appboycdn.com/web-sdk/latest/doc/modules/braze.html
  https://js.appboycdn.com/web-sdk/latest/doc/classes/braze.inappmessage.html
*/
export default class BrazeService {
  static readonly serviceName = 'BrazeService';

  braze: any = null;

  constructor() {
    /* 
    We do this because the mParticle kit sets up a in app message subscription that calls showInAppMessage
    see function primeBrazeWebPush in node_modules/@mparticle/web-braze-kit/dist/BrazeKit.common.js
    we could unsubscribe asap, but there still a chance a message could go through before we unsubscribe,
    instead manually override subscribeToInAppMessage with our own behaviour
    */
    const subscribeToInAppMessageRef = window.braze.subscribeToInAppMessage;
    window.braze = {
      ...window.braze,
      subscribeToInAppMessage: (/* throw away passed in function */) =>
        subscribeToInAppMessageRef((inAppMessage: BrazeMessage) => {
          if (!this.braze) return;

          // control group messages will log an impression and not show a visible message
          if (inAppMessage instanceof this.braze.ControlMessage) {
            return this.logControlGroupImpression(inAppMessage);
          }

          const onViewed = () => {
            this.braze.logInAppMessageImpression(inAppMessage);
          };

          // Ignore any messages that has no buttons, eg. Web's survey message in HtmlMessage type
          if (inAppMessage.buttons.length === 0) return;

          const onButtonClick = (index: number) => {
            const button = inAppMessage.buttons[index];

            if (button) {
              this.braze.logInAppMessageButtonClick(button, inAppMessage);
            }
          };

          const displayType =
            inAppMessage instanceof this.braze.FullScreenMessage
              ? 'fullscreen'
              : 'notification';

          const messageProps: MessageCallbackProps = {
            message: inAppMessage,
            displayType,
            onViewed,
            onButtonClick,
          };

          AppData?.notificationService.show(
            WidgetNames.BRAZE_NOTIFICATION,
            messageProps,
          );
        }),
    };
  }

  init(brazeInstance: any) {
    if (!brazeInstance) {
      reportError({
        error: new Error('Braze Instance Not Defined'),
        userFacing: false,
        code: ErrorCode.c49,
      });
      return;
    }

    this.braze = brazeInstance;

    // the mParticle kit handles the initialization
  }

  private logControlGroupImpression(controlGroupMessage: unknown) {
    // despite the name, showInAppMessage for control group messages won't actually display a message modal to the user
    this.braze.showInAppMessage(controlGroupMessage);
  }
}
